import { useState } from 'react';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { InboxVariant } from './inbox-variant';
import { InboxMediaUpload } from './InboxMediaUpload';

type MediaSource = 'none' | 'upload';

function initialSource(value: InboxVariant): MediaSource {
  if (value.videoUrl.trim() || value.imageUrl.trim()) return 'upload';
  return 'none';
}

export function InboxMediaSelector({
  value,
  onChange,
  disabled,
}: {
  value: InboxVariant;
  onChange: (variant: InboxVariant) => void;
  disabled?: boolean;
}) {
  const [source, setSource] = useState<MediaSource>(() => initialSource(value));

  const selectSource = (nextValues: string[]) => {
    const next = nextValues[0] as MediaSource | undefined;
    if (!next || next === source) return;
    setSource(next);
    onChange({
      ...value,
      category: next === 'none' ? 'Message' : 'Promotion',
      imageUrl: '',
      videoUrl: '',
    });
  };

  return (
    <FieldGroup>
      <Field>
        <FieldLabel>Media (optional)</FieldLabel>
        <ToggleGroup
          value={[source]}
          onValueChange={selectSource}
          disabled={disabled}
          variant="outline"
          className="flex-wrap"
          aria-label="Inbox media source"
        >
          <ToggleGroupItem value="none">No media</ToggleGroupItem>
          <ToggleGroupItem value="upload">Upload</ToggleGroupItem>
        </ToggleGroup>
        <p className="text-xs text-muted-foreground">
          Upload campaign media to the approved storage bucket, or send a
          message without media. Changing the source clears the previous
          media.
        </p>
      </Field>

      {source === 'upload' && (
        <Field>
          <InboxMediaUpload
            imageUrl={value.imageUrl}
            videoUrl={value.videoUrl}
            onChange={(patch) =>
              onChange({
                ...value,
                category: 'Promotion',
                imageUrl: patch.imageUrl ?? '',
                videoUrl: patch.videoUrl ?? '',
              })
            }
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">
            Upload a JPG, PNG or WebP image, or an MP4/WebM video. The upload
            becomes the public HTTPS URL sent to the app.
          </p>
        </Field>
      )}
    </FieldGroup>
  );
}
